import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineFileExcel } from "react-icons/ai";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { FiBookOpen } from "react-icons/fi";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { Month } from "../../OtherData/Month";
import { errorAlert, infoAlert } from "../../Components/Alerts";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { WagesReportExcel } from "../../Print/WagesReportExcel";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import "./TaxWagesReportPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const emptyTotals = {
  pagaBruto: 0,
  kontributiPunetorit: 0,
  kontributiPunedhenesit: 0,
  kontributiSuplementarPunesuarit: 0,
  kontributiSuplementarPunedhenesit: 0,
  pagaNeto: 0,
};

const toNumber = (value) => Number.parseFloat(value) || 0;

const buildTotals = (items) => items.reduce((acc, item) => ({
  pagaBruto: acc.pagaBruto + toNumber(item.PagaBruto),
  kontributiPunetorit: acc.kontributiPunetorit + toNumber(item.KontributiPunetorit),
  kontributiPunedhenesit: acc.kontributiPunedhenesit + toNumber(item.KontributiPunedhenesit),
  kontributiSuplementarPunesuarit: acc.kontributiSuplementarPunesuarit + 0,
  kontributiSuplementarPunedhenesit: acc.kontributiSuplementarPunedhenesit + 0,
  pagaNeto: acc.pagaNeto + toNumber(item.PagaNeto),
}), { ...emptyTotals });

export default function TaxWagesReportPage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [data, setData] = useState([]);
  const [month, setMonth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totals, setTotals] = useState({ ...emptyTotals });

  const fetchWagesRows = async (page, pageSize) => {
    if (!month || month < 1 || month > 12) {
      return { items: [], total: 0 };
    }

    const response = await axios.get(ApiLink.API_URL + "TaxReport/TaxWagesReport-grid", {
      headers,
      params: {
        month,
        fiscalyear: fiscalYear,
        page,
        pageSize,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items || []),
        quickFilter: quickFilter.join(" "),
      },
    });

    const payload = response?.data;
    const items = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
    const total = Array.isArray(payload)
      ? items.length
      : Number(payload?.total ?? payload?.totalRows ?? items.length);

    return { items, total };
  };

  const fetchData = async () => {
    if (!month || month < 1 || month > 12) {
      setData([]);
      setRowCount(0);
      setTotals({ ...emptyTotals });
      return;
    }

    setLoading(true);
    try {
      const { items, total } = await fetchWagesRows(paginationModel.page, paginationModel.pageSize);
      setData(items);
      setRowCount(total);

      try {
        const { items: allItems } = await fetchWagesRows(0, Math.max(total, items.length, 1));
        setTotals(buildTotals(allItems));
      } catch {
        setTotals({ ...emptyTotals });
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        infoAlert("Ju nuk keni qasje në këto të dhëna.");
      } else if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, fiscalYear, paginationModel, sortModel, filterModel, quickFilter]);

  const columns = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "emri", headerName: "Emri", minWidth: 150, width: 170 },
    { field: "mbiemri", headerName: "Mbiemri", minWidth: 150, width: 170 },
    { field: "numripersonal", headerName: "Numri Individual i punëtorit", minWidth: 180, width: 220 },
    {
      field: "pagabruto",
      headerName: "Bruto paga për muaj",
      minWidth: 150,
      width: 180,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "kontributipunetorit",
      headerName: "Kontributi pensional i të punësuarit",
      minWidth: 200,
      width: 240,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "kontributipunedhenesit",
      headerName: "Kontributi pensional i punëdhënësit",
      minWidth: 200,
      width: 240,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "kontributisuplementarpunesuarit",
      headerName: "Kontributi suplementar i të punësuarit",
      minWidth: 200,
      width: 240,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "kontributisuplementarpunedhenesit",
      headerName: "Kontributi suplementar i punëdhënësit",
      minWidth: 200,
      width: 240,
      valueFormatter: (value) => formatCurrency(value),
    },
    { field: "puneprimare", headerName: "Punë Primare", minWidth: 140, width: 160 },
    { field: "perfshihenkontributet", headerName: "Përfshihen Kontributet", minWidth: 170, width: 200 },
    { field: "aplikohettatiminepaga", headerName: "Aplikohet Tatimi ne Paga", minWidth: 170, width: 210 },
  ]), []);

  const rows = useMemo(() => (
    data.map((item, index) => ({
      id: item.NumriRendor || `${item.NumriPersonal || "wage"}-${index}`,
      rowNumber: item.NumriRendor || (paginationModel.page * paginationModel.pageSize + index + 1),
      emri: item.Emri || "",
      mbiemri: item.Mbiemri || "",
      numripersonal: item.NumriPersonal || "",
      pagabruto: item.PagaBruto || 0,
      kontributipunedhenesit: item.KontributiPunedhenesit || 0,
      kontributipunetorit: item.KontributiPunetorit || 0,
      kontributisuplementarpunesuarit: 0,
      kontributisuplementarpunedhenesit: 0,
      puneprimare: item.PunePrimare || "",
      perfshihenkontributet: item.PerfshihenKontributet || "",
      aplikohettatiminepaga: item.AplikohetTatimiNePaga || "",
      rowInfo: item,
    }))
  ), [data, paginationModel.page, paginationModel.pageSize]);

  const fetchAllRowsForExport = async () => {
    if (!month || month < 1 || month > 12) {
      return [];
    }

    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
    const response = await axios.get(ApiLink.API_URL + "TaxReport/TaxWagesReport-grid", {
      headers,
      params: {
        month,
        fiscalyear: fiscalYear,
        page: 0,
        pageSize: totalRowsToExport,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items || []),
        quickFilter: quickFilter.join(" "),
      },
    });

    const payload = response?.data;
    if (Array.isArray(payload)) {
      return payload;
    }

    return payload?.items || payload?.data || [];
  };

  const handleDownloadExcel = async () => {
    try {
      const exportRows = await fetchAllRowsForExport();
      if (!exportRows.length) {
        infoAlert("Nuk ka të dhëna për eksport.");
        return;
      }

      const workbook = WagesReportExcel(exportRows);
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `WM_CM_${month}_${fiscalYear}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në eksport: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur gjatë eksportit.");
      }
    }
  };

  const hasSelectedPeriod = month && month >= 1 && month <= 12;
  const emptyGridMessage = hasSelectedPeriod
    ? "Nuk ka te dhena."
    : "Ju lutem zgjidhni muajin.";

  return (
    <div className="Crud__Container TWRP__page">
      <div className="TWRP__content">
        <CrudPageHeader
          icon={FiBookOpen}
          title="Raporti Mujor i Pagave për ATK"
          description="Ky format i raportit të pagave është i përshtatur për ngarkim në sistemin e ATK-së."
          className="TWRP__header"
        />

        <div className="TWRP__reportGrid">
          <ReusableAccountBooksGrid
            columns={columns}
            rows={rows}
            loading={loading}
            rowCount={rowCount}
            height={650}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={setSortModel}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            showQuickSearch={false}
            showExportButton={false}
            toolbarActions={(
              <AddActionsButton
                text="XLSX"
                onClick={handleDownloadExcel}
                icon={<AiOutlineFileExcel />}
                className="RSDG__toolButton TWRP__gridActionBtn--xlsx"
              />
            )}
            toolbarRightActions={(
              <TextField
                className="TWRP__toolbarField TWRP__toolbarField--month"
                select
                size="small"
                label="Muaji"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                required
              >
                {Month.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {fiscalYear} {item.month}
                  </MenuItem>
                ))}
              </TextField>
            )}
            showViewAction={false}
            showEditAction={false}
            showDeleteAction={false}
            emptyMessage={emptyGridMessage}
            summaryFooter={{
              labelField: "numripersonal",
              label: "Totali",
              values: {
                pagabruto: formatCurrency(totals.pagaBruto),
                kontributipunetorit: formatCurrency(totals.kontributiPunetorit),
                kontributipunedhenesit: formatCurrency(totals.kontributiPunedhenesit),
                kontributisuplementarpunesuarit: formatCurrency(totals.kontributiSuplementarPunesuarit),
                kontributisuplementarpunedhenesit: formatCurrency(totals.kontributiSuplementarPunedhenesit),
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}